<template>
  <div class="schedule-page">
    <!-- 操作栏 -->
    <div class="action-bar">
      <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">添加任务</el-button>
    </div>

    <!-- 任务列表 -->
    <el-table v-loading="loading" :data="tableData" border stripe style="width: 100%">
      <el-table-column prop="id" label="ID" width="55" align="center" />
      <el-table-column prop="jobName" label="任务名称" width="120" show-overflow-tooltip />
      <el-table-column prop="beanName" label="Bean名称" width="160" show-overflow-tooltip />
      <el-table-column prop="methodName" label="方法名" width="80" align="center" />
      <el-table-column prop="cronExpression" label="Cron表达式" width="100" />
      <el-table-column label="状态" width="150" align="center">
        <template slot-scope="{ row }">
          <el-switch
            :value="row.status"
            :active-value="1"
            :inactive-value="0"
            active-text="正常"
            inactive-text="暂停"
            @change="onStatusChange(row)"
          />
        </template>
      </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip />
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" :disabled="row.status === 1" @click="handleEdit(row)">编辑</el-button>
          <el-button type="text" size="small" :disabled="row.status === 0" class="trigger-btn" @click="handleTrigger(row)">触发</el-button>
          <el-button type="text" size="small" class="delete-btn" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑弹窗 -->
    <el-dialog :title="editForm.id ? '编辑任务' : '添加任务'" :visible.sync="dialogVisible" :close-on-click-modal="false" width="560px">
      <el-form ref="editFormRef" :model="editForm" :rules="formRules" label-width="100px" label-position="right">
        <el-form-item label="任务名称" prop="jobName">
          <el-input v-model="editForm.jobName" placeholder="如：科脉会员同步" />
        </el-form-item>
        <el-form-item label="Bean名称" prop="beanName">
          <el-input v-model="editForm.beanName" placeholder="Spring Bean 名称（需注册为Bean）" />
        </el-form-item>
        <el-form-item label="方法名" prop="methodName">
          <el-input v-model="editForm.methodName" placeholder="要调用的方法名" />
        </el-form-item>
        <el-form-item label="Cron表达式" prop="cronExpression">
          <el-input v-model="editForm.cronExpression" placeholder="0 0 2 * * ?" clearable />
          <div class="cron-hint">常用示例: 每30分: 0/30 * * * * ? | 每小时: 0 0 * * * * ?</div>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="editForm.status">
            <el-radio :label="0">暂停</el-radio>
            <el-radio :label="1">立即启动</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="参数(JSON)" prop="params">
          <el-input v-model="editForm.params" type="textarea" :rows="3" placeholder='可选，JSON格式参数' clearable />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="editForm.remark" placeholder="可选备注" clearable />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { jobList, jobAdd, jobUpdate, jobDelete, jobStart, jobSuspend, jobTrig } from '@/api/schedule'

export default {
  name: 'ScheduleManage',
  data() {
    return {
      loading: false,
      submitting: false,
      dialogVisible: false,
      tableData: [],
      editForm: this.getEmptyForm(),
      formRules: {
        jobName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        beanName: [{ required: true, message: '请输入Bean名称', trigger: 'blur' }],
        methodName: [{ required: true, message: '请输入方法名', trigger: 'blur' }],
        cronExpression: [{ required: true, message: '请输入Cron表达式', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.loading = true
      jobList().then(res => {
        this.tableData = res.data || []
      }).catch(() => {
        this.tableData = []
      }).finally(() => {
        this.loading = false
      })
    },

    getEmptyForm() {
      return { id: null, jobName: '', beanName: '', methodName: '', cronExpression: '0 0 2 * * ?', params: '', status: 0, remark: '', jobGroup: 'DEFAULT' }
    },

    handleAdd() {
      this.editForm = this.getEmptyForm()
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.editForm = { ...row }
      this.dialogVisible = true
    },

    onStatusChange(row) {
      const action = row.status === 1 ? jobSuspend : jobStart
      action(row.id).then(() => {
        this.$message.success('操作成功')
        this.getList()
      }).catch(() => {
        // 回滚状态显示
        row.status = row.status === 1 ? 0 : 1
      })
    },

    handleTrigger(row) {
      jobTrig(row.id).then(() => {
        this.$message.success('已触发执行')
      }).catch(() => {})
    },

    handleDelete(row) {
      this.$confirm(`确认删除任务「${row.jobName}」吗？`, '提示', { confirmButtonText: '确定删除', type: 'warning' }).then(() => {
        jobDelete(row.id).then(() => {
          this.$message.success('删除成功')
          this.getList()
        })
      }).catch(() => {})
    },

    handleSubmit() {
      this.$refs.editFormRef.validate(valid => {
        if (!valid) return
        this.submitting = true
        const action = this.editForm.id ? jobUpdate : jobAdd
        action(this.editForm).then(() => {
          this.submitting = false
          this.dialogVisible = false
          this.$message.success('保存成功')
          this.getList()
        }).catch(() => {
          this.submitting = false
        })
      })
    }
  }
}
</script>

<style lang="less" scoped>
.schedule-page {
  padding: 20px;
}
.action-bar {
  margin-bottom: 16px;
}
.trigger-btn { color: #e6a23c; }
.delete-btn { color: #f56c6c; }
.cron-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  margin-top: 4px;
}
</style>
